import React, { useEffect, useRef, useState } from 'react'
import { FormattedMessage, useIntl } from 'react-intl'
import CloseIcon from '@mui/icons-material/Close'
import { CustomButton } from '~/components/CustomButton/CustomButton'
import { useConfiguratorConfigOptional } from '~/context/urlConfigContext'
import { useLocale } from '~/hooks/useLocale'
import { buildConfigShareUrl } from '~/utils/configShareUrl'
import type { ProductKey } from '~/utils/configUrl'
import { trackPromoPopup } from '~/utils/analytics'
import { LeadErrorNotice } from '~/components/LeadErrorNotice/LeadErrorNotice'
import { promoChatBus, markPhoneLeft, recordPromoConsent } from './promoChatBus'
import { submitPromoLead } from './submitPromoLead'
import { usePromoPopupTrigger, DISMISSED_KEY } from './usePromoPopupTrigger'
import styles from './PromoLeadPopup.module.css'

// Converts an engaged-but-stalled configurator visitor into a lead: shows a
// bottom sheet during a pause with a phone input; submitting POSTs the number
// (the chat socket has no polling fallback, so it cannot be the transport for
// a lead) and only then hands the project link to the chat for an instant
// auto-reply. With no chat connected the sheet confirms the lead itself.
export const PromoLeadPopup: React.FC<{
  product?: ProductKey
  /** Catalog-style pages: count sustained scrolling as engagement. */
  scrollEngagement?: boolean
}> = ({ product, scrollEngagement }) => {
  const intl = useIntl()
  const locale = useLocale()
  const ctx = useConfiguratorConfigOptional()
  const [phone, setPhone] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [sendFailed, setSendFailed] = useState(false)
  const [confirmed, setConfirmed] = useState(false)
  const phoneInputRef = useRef<HTMLInputElement>(null)

  const configJson = ctx && product ? JSON.stringify(ctx.config) : null
  const { visible, hide } = usePromoPopupTrigger(
    configJson,
    scrollEngagement ?? false
  )

  useEffect(() => {
    promoChatBus.state.popupVisible = visible
    if (visible) {
      trackPromoPopup('popup_shown', { pagePath: window.location.pathname })
    }
    return () => {
      promoChatBus.state.popupVisible = false
    }
  }, [visible])

  // Only the explicit ✕ closes the popup; it starts the 7-day cooldown so it
  // won't re-show for a week. Tapping outside the sheet no longer dismisses it.
  const handleClose = () => {
    // Closing the confirmation is not a rejection: no cooldown stamp, no
    // `popup_dismissed`. markPhoneLeft() already retired the popup for good.
    if (confirmed) {
      hide()
      return
    }
    try {
      localStorage.setItem(DISMISSED_KEY, String(Date.now()))
    } catch {
      /* ignore */
    }
    trackPromoPopup('popup_dismissed', { pagePath: window.location.pathname })
    hide()
  }

  if (!visible) return null

  const projectUrl = (): string =>
    ctx && product
      ? buildConfigShareUrl(ctx.config, product, locale)
      : window.location.href

  // The button stays visually active while idle: MUI's .Mui-disabled washes the
  // label into the pill on real devices. An empty tap just focuses the input.
  const handleSubmit = async () => {
    if (isSubmitting) return
    if (phone.trim().length === 0) {
      phoneInputRef.current?.focus()
      return
    }

    const url = projectUrl()
    trackPromoPopup('popup_cta_click', { pagePath: window.location.pathname })

    setSendFailed(false)
    setIsSubmitting(true)
    try {
      await submitPromoLead({ phone: phone.trim(), projectUrl: url })
    } catch (err) {
      console.error('[promo-popup] /api/contact-form failed', err)
      setSendFailed(true)
      setIsSubmitting(false)
      return
    }
    setIsSubmitting(false)

    // Past this line the lead is on the server. The bookkeeping below used to
    // live in FloatingMessenger and therefore never ran on a dead socket.
    markPhoneLeft()
    recordPromoConsent()
    trackPromoPopup('popup_lead', { pagePath: window.location.pathname })

    // The chat, when connected, answers with the project link — that is the
    // visitor's confirmation and the sheet can go. Otherwise the sheet owes
    // them the confirmation itself.
    if (promoChatBus.request({ projectUrl: url, phone: phone.trim() })) hide()
    else setConfirmed(true)
  }

  return (
    <div
      className={styles.sheet}
      role="dialog"
      aria-modal="false"
      aria-labelledby="promo-popup-title"
      data-testid="promo-popup"
    >
      <button
        type="button"
        className={styles.closeButton}
        aria-label={intl.formatMessage({ id: 'promoPopup.close' })}
        onClick={handleClose}
        data-testid="promo-popup-close"
      >
        <CloseIcon fontSize="inherit" />
      </button>
      {/* Before/after crossfade of the same room frame — the "your wardrobe
          in your interior" magnet. Fixed-height box + explicit img sizes:
          zero layout shift; eager + high priority since it's the popup hero. */}
      <div className={styles.mediaBox} aria-hidden="true">
        <img
          className={styles.mediaFrame}
          src="/cdn/promo/promo-before.webp"
          alt=""
          width={800}
          height={1192}
          fetchPriority="high"
          decoding="async"
        />
        <div className={styles.mediaAfter}>
          <img
            className={styles.mediaFrame}
            src="/cdn/promo/promo-after.webp"
            alt=""
            width={400}
            height={596}
            fetchPriority="high"
            decoding="async"
          />
          <span className={styles.mediaBadge}>
            <FormattedMessage id="promoPopup.badge3d" />
          </span>
        </div>
      </div>
      <div className={styles.content}>
        <h3 id="promo-popup-title" className={styles.title}>
          <FormattedMessage
            id={confirmed ? 'promoPopup.success.title' : 'promoPopup.title'}
          />
        </h3>
        <p className={styles.body}>
          <FormattedMessage
            id={confirmed ? 'promoPopup.success.body' : 'promoPopup.body'}
          />
        </p>
        {!confirmed && (
          <>
            <input
              ref={phoneInputRef}
              className={styles.phoneInput}
              type="tel"
              inputMode="tel"
              autoComplete="tel"
              value={phone}
              placeholder={intl.formatMessage({
                id: 'promoPopup.phonePlaceholder',
              })}
              onChange={(e) => setPhone(e.target.value)}
              onKeyDown={(e) =>
                e.key === 'Enter' && (e.preventDefault(), handleSubmit())
              }
              data-testid="promo-popup-phone"
            />
            <p className={styles.consent}>
              <FormattedMessage id="promoPopup.consent" />
            </p>
            {sendFailed && (
              <LeadErrorNotice
                messageId="promoPopup.error.sendFailed"
                prefillId="promoPopup.whatsapp.prefill"
                prefillValues={{ url: projectUrl() }}
                className={styles.error}
                testId="promo-popup-error"
              />
            )}
            <div className={styles.ctaRow}>
              <CustomButton
                variant="primary"
                size="medium"
                onClick={handleSubmit}
                disabled={isSubmitting}
                data-testid="promo-popup-cta"
              >
                <FormattedMessage
                  id={
                    isSubmitting ? 'homepage.button.sending' : 'promoPopup.cta'
                  }
                />
              </CustomButton>
            </div>
          </>
        )}
      </div>
    </div>
  )
}
